import { z } from 'zod';

import { AddressValidationRulesQuery } from '../../saleor-graphql';
import { schemaEmail, schemaAddressAll } from '../schemas';

function camelCaseToWords(s: string) {
  const result = s.replace(/([A-Z0-9])/g, ' $1');

  return result.charAt(0).toUpperCase() + result.slice(1);
}

type FieldChoice = { raw: string; verbose: string };
type FieldValidation = 
  | z.ZodString 
  | z.ZodOptional<z.ZodNullable<z.ZodString>> 
  | z.ZodLiteral<string> 
  | z.ZodOptional<z.ZodNullable<z.ZodLiteral<string>>> 
  | z.ZodEnum<[string, ...string[]]> 
  | z.ZodOptional<z.ZodNullable<z.ZodEnum<[string, ...string[]]>>>;

const fieldRulesToSchema = (
  field: string,
  rules: AddressValidationRulesQuery['addressValidationRules']
): FieldValidation => {
  const required = rules?.requiredFields?.includes(field);
  const fieldTypeLabel = (rules as Record<string, unknown>)?.[`${field}Type`] as string | undefined;
  const fieldDisplayName = camelCaseToWords(fieldTypeLabel || field);
  const fieldChoices = (rules as Record<string, unknown>)?.[`${field}Choices`] as FieldChoice[] | undefined;

  // Base validation
  let rule: FieldValidation = required
    ? z.string().min(1, `${fieldDisplayName} is required`)
    : z.string().nullish();

  // Add choice validation if choices exist
  if (fieldChoices && fieldChoices.length > 0) {
    const rawValues = fieldChoices.map(choice => choice.raw);
    
    if (rawValues.length === 1) {
      // Single choice - use literal
      const literalRule = z.literal(rawValues[0]);

      rule = required 
        ? literalRule.describe(`${fieldDisplayName} must be ${fieldChoices[0].verbose}`)
        : z.union([z.literal(''), literalRule]).nullish();
    } else {
      // Multiple choices - use enum
      const enumRule = z.enum(rawValues as [string, ...string[]]);

      rule = required
        ? enumRule.describe(`${fieldDisplayName} must be one of the available options`)
        : z.union([z.literal(''), enumRule]).nullish();
    }
  }

  // Add postal code validation
  if (field === 'postalCode' && rules?.postalCodeMatchers && rules.postalCodeMatchers.length > 0) {
    const postalCodeRegex = new RegExp(rules.postalCodeMatchers.join('|'));
    const postalCodeType = rules.postalCodeType || 'postal code';
    
    if (required) {
      rule = z.string()
        .min(1, `${camelCaseToWords(postalCodeType)} is required`)
        .regex(postalCodeRegex, `Invalid ${postalCodeType.toLowerCase()} format`);
    } else {
      rule = z.string()
        .regex(postalCodeRegex, `Invalid ${postalCodeType.toLowerCase()} format`)
        .nullish();
    }
  }

  return rule;
};

const rulesToSchema = (rules: AddressValidationRulesQuery['addressValidationRules']) => {
  const fields: Record<string, FieldValidation> = {};

  rules?.allowedFields.forEach((field) => {
    fields[field] = fieldRulesToSchema(field, rules);
  });

  return schemaAddressAll.merge(z.object(fields));
};

export default rulesToSchema;

export const constructSchema = (
  validationRules: AddressValidationRulesQuery['addressValidationRules']
) => {
  const schemaAddress = rulesToSchema(validationRules);

  const schemaForm = schemaEmail.merge(schemaAddress);

  return schemaForm;
};